import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, NetworkError, api } from './client.js'
import type { Session } from './types.js'

const CACHE_KEY = 'hauswerk.session'

/**
 * The last known session, kept so the app still renders when it is opened with
 * no signal.
 *
 * Without this a worker opening the app in a cellar gets a failed `/auth/me`,
 * no role, and is bounced to the backoffice login screen, with their day's
 * tasks sitting in the cache right behind it. This is a convenience only: every
 * request is still authorised by the cookie on the server.
 */
function readCache(): Session | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    return raw ? (JSON.parse(raw) as Session) : null
  } catch {
    return null
  }
}

function writeCache(session: Session | null): void {
  try {
    if (session) localStorage.setItem(CACHE_KEY, JSON.stringify(session))
    else localStorage.removeItem(CACHE_KEY)
  } catch {
    /* private mode, storage disabled, the app works, it just re-authenticates */
  }
}

export function useSession() {
  return useQuery<Session | null>({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        const session = await api<Session>('/api/auth/me')
        writeCache(session)
        return session
      } catch (error) {
        // Offline: keep whoever was signed in on this device.
        if (error instanceof NetworkError) return readCache()
        // Signed out or session expired: a normal state, and the cache must go.
        if (error instanceof ApiError && error.status === 401) {
          writeCache(null)
          return null
        }
        throw error
      }
    },
    initialData: readCache,
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export function useClearSession() {
  const queryClient = useQueryClient()
  return async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => {})
    writeCache(null)
    queryClient.clear()
  }
}
